import { createContext, useCallback, useEffect, useMemo } from "react";
import axios from "../../utils/axiosV2";
// settings
import actionTypes from "../../common/actionTypes";
import { initialState as shopInitialState } from "../../common/V2/shopConstants";
// context
import useAuthContext from "../../hooks/useAuthContext";
// reducers
import { shopReducer } from "../../reducers/V2/shopReducer";
// hooks
import useCustomReducer from "../../hooks/useCustomReducer";

const SHOP_DB_INFO = "/v2/shop";
const SHOP_DOMAIN = "/v2/shop/domain";
const SHOP_DOMAIN_VALIDATE = "/v2/shop/domain/validate";
const SHOP_THEME = "/v2/shop/theme";
const SHOP_SETTINGS = "/v2/shop/settings";
const SHOP_SUBSCRIPTION_UPDATE = "/v2/shop/subscription/update";
const SHOP_SUBSCRIPTION_VALIDATE = "/v2/shop/subscription/validate";
const SHOP_SUBSCRIPTION_CANCEL = "/v2/shop/subscription/cancel";

const initialState = {
  shop: { ...shopInitialState },
};

const ShopContext = createContext({
  ...initialState,
});

const ShopProvider = ({ children }) => {
  const [state, dispatch] = useCustomReducer(shopReducer, initialState);
  const { user } = useAuthContext();

  const fetchShop = useCallback(async () => {
    dispatch({ type: actionTypes.FETCH_SHOP });

    try {
      const { data } = await axios.get(SHOP_DB_INFO);
      const shop = data.data.existingShop;
      dispatch({
        type: actionTypes.FETCH_SHOP_SUCCESS,
        payload: { shop },
      });
    } catch (error) {
      dispatch({
        type: actionTypes.FETCH_SHOP_FAILURE,
        error,
      });
    }
  }, [dispatch]);

  useEffect(() => {
    if (user?.domain) {
      fetchShop();
    }
  }, [user?.domain, fetchShop]);

  const createDNSRecordInDB = async (email) => {
    try {
      const res = await axios.post(`${SHOP_DOMAIN}`, {
        email,
      });

      if (!res.data || !res.data.success) {
        throw new Error(res.data?.error || "Failed to create DNS records");
      }

      // Update the shop context with the new custom domain data
      dispatch({
        type: actionTypes.UPDATE_CUSTOM_DOMAIN,
        payload: res.data.data,
      });

      // Return the DNS records from the server response
      return res.data.data.dnsRecords || [];
    } catch (error) {
      // Handle specific server errors
      if (error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      throw error;
    }
  };

  const validateDNSRecords = async () => {
    try {
      const res = await axios.post(`${SHOP_DOMAIN_VALIDATE}`);

      if (!res.data || !res.data.success) {
        throw new Error(res.data?.error || "DNS validation failed");
      }

      // Update the shop context with the updated custom domain data if available
      if (res.data.data.updatedDnsRecords || res.data.data.isValid !== undefined) {
        dispatch({
          type: actionTypes.UPDATE_CUSTOM_DOMAIN,
          payload: res.data.data,
        });
      }

      return {
        success: res.data.success,
        isValid: res.data.data.isValid,
        status: res.data.data.status,
        validationDetails: res.data.data.validationDetails,
        updatedDnsRecords: res.data.data.updatedDnsRecords,
      };
    } catch (error) {
      // Handle specific server errors
      if (error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }

      throw error;
    }
  };

  const getDNSRecords = async () => {
    try {
      dispatch({
        type: actionTypes.FETCH_CUSTOM_DOMAIN,
      });
      const res = await axios.get(`${SHOP_DOMAIN}`);
      dispatch({
        type: actionTypes.FETCH_CUSTOM_DOMAIN_SUCCESS,
        payload: res.data.data,
      });
    } catch (error) {
      dispatch({
        type: actionTypes.FETCH_CUSTOM_DOMAIN_FAILURE,
        error: error,
      });
      throw error;
    }
  };

  const updateThemeExtensionStatus = async (isEnabled) => {
    try {
      const res = await axios.post(`${SHOP_THEME}`, { isEnabled });

      if (!res.data || !res.data.success) {
        throw new Error(res.data?.error || "Failed to update theme extension status");
      }

      // Update the shop context with the new isEnabled status
      dispatch({
        type: actionTypes.UPDATE_THEME_EXTENSION_STATUS,
        payload: { isEnabled },
      });

      return res.data;
    } catch (error) {
      console.error("Error updating theme extension status:", error);
      if (error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      throw error;
    }
  };

  const updateShop = useCallback(
    async (shopData) => {
      try {
        const res = await axios.put(SHOP_DB_INFO, shopData);

        if (!res.data || !res.data.success) {
          throw new Error(res.data?.error || "Failed to update shop");
        }

        dispatch({
          type: actionTypes.UPDATE_SHOP,
          payload: res.data.data,
        });

        return res.data;
      } catch (error) {
        if (error.response?.data?.error) {
          throw new Error(error.response.data.error);
        }
        throw error;
      }
    },
    [dispatch]
  );

  const updateShopSettings = useCallback(
    async (selectors) => {
      try {
        const res = await axios.put(SHOP_SETTINGS, { selectors });

        if (!res.data || !res.data.success) {
          throw new Error(res.data?.error || "Failed to update settings");
        }

        dispatch({
          type: actionTypes.UPDATE_SHOP_SELECTORS,
          payload: res.data.data.selectors,
        });

        return res.data;
      } catch (error) {
        if (error.response?.data?.error) {
          throw new Error(error.response.data.error);
        }
        throw error;
      }
    },
    [dispatch]
  );

  const updateSubscription = useCallback(
    async (subscriptionInfo) => {
      try {
        const { data } = await axios.post(SHOP_SUBSCRIPTION_UPDATE, subscriptionInfo);

        if (data.success && data.data?.confirmationUrl) {
          dispatch({
            type: actionTypes.STORE_SUBSCRIBE_CONFIRMATION_URL,
            payload: data.data.confirmationUrl,
          });
        }

        return data;
      } catch (error) {
        if (error.response?.data?.message) {
          throw new Error(error.response.data.message);
        }
        throw error;
      }
    },
    [dispatch]
  );

  const validateSubscription = useCallback(async (subscriptionInfo) => {
    try {
      const { data } = await axios.post(SHOP_SUBSCRIPTION_VALIDATE, subscriptionInfo);
      return data;
    } catch (error) {
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw error;
    }
  }, []);

  const cancelSubscription = useCallback(async () => {
    try {
      const { data } = await axios.post(SHOP_SUBSCRIPTION_CANCEL);
      return data;
    } catch (error) {
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw error;
    }
  }, []);

  const removeSubscribeConfirmUrl = useCallback(() => {
    dispatch({ type: actionTypes.REMOVE_SUBSCRIBE_CONFIRMATION_URL });
  }, [dispatch]);

  const memoizedState = useMemo(
    () => ({
      ...state,
      createDNSRecordInDB,
      getDNSRecords,
      validateDNSRecords,
      updateThemeExtensionStatus,
      updateShop,
      updateShopSettings,
      updateSubscription,
      validateSubscription,
      cancelSubscription,
      removeSubscribeConfirmUrl,
      fetchShop,
    }),
    [
      state,
      updateShop,
      updateShopSettings,
      updateSubscription,
      validateSubscription,
      cancelSubscription,
      removeSubscribeConfirmUrl,
      fetchShop,
    ]
  );

  return <ShopContext.Provider value={memoizedState}>{children}</ShopContext.Provider>;
};

export { ShopContext, ShopProvider };
